import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import StaffEquipmentDialog from "@/components/app/StaffEquipmentDialog";
import QrCodeDialog from "@/components/app/QrCodeDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Wind, QrCode, Trash2, Hash, User, Package } from "lucide-react";

export default function StaffEquipmentTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReg, setOpenReg] = useState(false);
  const [qrEquip, setQrEquip] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/equipment");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao carregar equipamentos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = (equip) => { load(); if (equip) setQrEquip(equip); };

  const remove = async (id) => {
    try {
      await api.delete(`/staff/equipment/${id}`);
      toast.success("Equipamento removido.");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Cadastre aparelhos e gere QR Codes com o histórico de serviços (instalação, conserto, manutenção).</p>
        <Button className="bg-blue-600 hover:bg-blue-700 gap-1.5 flex-shrink-0" onClick={() => setOpenReg(true)} data-testid="staff-add-equipment-button">
          <Plus className="h-4 w-4" /> Cadastrar equipamento
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm">Carregando...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="staff-equipment-empty">
          <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Nenhum equipamento cadastrado ainda.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2" data-testid="staff-equipment-list">
          {items.map((eq) => (
            <Card key={eq.id} className="border-slate-200" data-testid={`staff-equipment-${eq.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Wind className="h-5 w-5 text-[#1e7be0]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800">{eq.equipment_type}{eq.brand ? ` · ${eq.brand}` : ""}{eq.model ? ` ${eq.model}` : ""}</p>
                    {eq.serial_number && <p className="text-sm text-slate-500 flex items-center gap-1"><Hash className="h-3.5 w-3.5" />{eq.serial_number}</p>}
                    {eq.customer_name && <p className="text-xs text-slate-500 flex items-center gap-1"><User className="h-3.5 w-3.5" />{eq.customer_name}</p>}
                    {eq.location && <p className="text-xs text-slate-400">{eq.location}</p>}
                  </div>
                  {eq.warranty?.type && (
                    <Badge variant="secondary" className="flex-shrink-0">{eq.warranty.type === "fabrica" ? "Garantia fábrica" : "Garantia serviço"}</Badge>
                  )}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="flex-1 gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50"
                    onClick={() => setQrEquip(eq)} data-testid={`staff-qr-${eq.id}`}>
                    <QrCode className="h-4 w-4" /> QR Code
                  </Button>
                  <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-500 flex-shrink-0"
                    onClick={() => setToDelete(eq)} data-testid={`staff-remove-equipment-${eq.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <StaffEquipmentDialog open={openReg} onOpenChange={setOpenReg} onSaved={onSaved} />
      <QrCodeDialog open={!!qrEquip} onOpenChange={(o) => !o && setQrEquip(null)}
        qrToken={qrEquip?.qr_token} equipment={qrEquip}
        equipmentLabel={qrEquip ? [qrEquip.equipment_type, qrEquip.brand, qrEquip.model].filter(Boolean).join(" ") : ""} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="equipment-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover equipamento?</AlertDialogTitle>
            <AlertDialogDescription>
              O equipamento <b>{[toDelete?.equipment_type, toDelete?.brand].filter(Boolean).join(" ")}</b> e o QR Code associado serão removidos. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="equipment-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => remove(toDelete.id)} data-testid="equipment-delete-confirm-button">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
